
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signUp: (email: string, password: string, firstName?: string, lastName?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);

        // Handle auth events
        if (event === 'SIGNED_IN') {
          toast({
            title: "Başarıyla giriş yaptınız",
            description: "Dernek Yönetim Merkezi'ne hoş geldiniz!",
          });
        } else if (event === 'SIGNED_OUT') {
          toast({
            title: "Çıkış yapıldı",
            description: "Güvenli bir şekilde çıkış yaptınız.",
          });
        }
      }
    );

    // THEN check for existing session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    return () => subscription.unsubscribe();
  }, [toast]);

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName,
            last_name: lastName,
          }
        }
      });

      if (error) {
        let errorMessage = 'Kayıt sırasında bir hata oluştu';
        if (error.message.includes('already registered')) {
          errorMessage = 'Bu email adresi zaten kayıtlı';
        } else if (error.message.includes('Password')) {
          errorMessage = 'Şifre en az 6 karakter olmalıdır';
        } else if (error.message.includes('Invalid email')) {
          errorMessage = 'Geçersiz email adresi';
        }
        
        toast({
          variant: "destructive",
          title: "Kayıt Hatası",
          description: errorMessage,
        });
      } else {
        toast({
          title: "Kayıt Başarılı",
          description: "Email adresinizi kontrol ederek hesabınızı doğrulayın.",
        });
      }

      return { error };
    } catch (error) {
      console.error('Signup error:', error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        let errorMessage = 'Giriş sırasında bir hata oluştu';
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'Geçersiz email veya şifre';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Email adresinizi doğrulamanız gerekiyor';
        }
        
        toast({
          variant: "destructive",
          title: "Giriş Hatası",
          description: errorMessage,
        });
      }

      return { error };
    } catch (error) {
      console.error('Signin error:', error);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Signout error:', error);
        toast({
          variant: "destructive",
          title: "Çıkış Hatası",
          description: "Çıkış yapılırken bir hata oluştu",
        });
      }
    } catch (error) {
      console.error('Signout error:', error);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const redirectUrl = `${window.location.origin}/auth?mode=reset`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        toast({
          variant: "destructive",
          title: "Şifre Sıfırlama Hatası",
          description: error.message,
        });
      } else {
        toast({
          title: "Şifre Sıfırlama",
          description: "Şifre sıfırlama bağlantısı email adresinize gönderildi.",
        });
      }

      return { error };
    } catch (error) {
      console.error('Password reset error:', error);
      return { error };
    }
  };

  const value = {
    user,
    session,
    isLoading,
    signUp,
    signIn,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
